import React, { useState, useEffect } from "react";
import Select from "react-select";
import PermissionsGroupService from "../../../services/v2/management/management-service";
import UsersService from "../../../services/v2/management/users-service";
import { useTheme } from "../../../context/ThemeContext";

const UserAdd = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    surname1: "",
    email: "",
    password: "",
    repeatPassword: "",
    selectedPermissionGroup: null,
  });
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (isOpen) {
      resetForm();
      fetchPermissionGroups();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      name: "",
      surname1: "",
      email: "",
      password: "",
      repeatPassword: "",
      selectedPermissionGroup: null,
    });
    setError(null);
  };

  const fetchPermissionGroups = async () => {
    try {
      const response = await PermissionsGroupService.list();
      const options = response.data.data.map((group) => ({
        label: group.name,
        value: group._id,
      }));
      setPermissionGroups(options);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const {
      name,
      surname1,
      email,
      password,
      repeatPassword,
      selectedPermissionGroup,
    } = formData;
    if (!name || !surname1 || !email || !password || !repeatPassword) {
      setError("All fields are required.");
      return;
    }
    if (password !== repeatPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!/^(?=.*[A-Z])(?=.*\d).{10,}$/.test(password)) {
      setError(
        "Password must be at least 10 characters long, contain at least one uppercase letter, and one number."
      );
      return;
    }
    const payload = {
      name,
      surname1,
      email,
      password,
      repeatPassword,
      permissionGroup: loadError
        ? null
        : selectedPermissionGroup?.value || null,
    };
    setLoading(true);
    setError(null);
    try {
      await UsersService.create(payload);
      onSuccess();
      onClose();
    } catch (error) {
      console.log(error);
      setError(
        error.response?.data?.message ||
          "Failed to create user. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: "40px",
      backgroundColor: isDarkMode ? "#1F2937" : "#FFFFFF",
      color: isDarkMode ? "#D1D5DB" : "#374151",
      borderColor: isDarkMode ? "#374151" : "#D1D5DB",
      boxShadow: "none",
      "&:hover": { borderColor: isDarkMode ? "#4F46E5" : "#6366F1" },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDarkMode ? "#1F2937" : "#FFFFFF",
      color: isDarkMode ? "#D1D5DB" : "#374151",
    }),
    option: (base, { isFocused }) => ({
      ...base,
      backgroundColor: isFocused
        ? isDarkMode
          ? "#374151"
          : "#E5E7EB"
        : "transparent",
      color: isDarkMode ? "#D1D5DB" : "#374151",
    }),
    placeholder: (base) => ({
      ...base,
      color: isDarkMode ? "#9CA3AF" : "#6B7280",
    }),
    singleValue: (base) => ({
      ...base,
      color: isDarkMode ? "#D1D5DB" : "#374151",
    }),
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-lg flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-2xl w-full p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Add New User
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Email
            </label>
            <input
              type="email"
              className="border rounded p-2 w-full h-12 dark:bg-gray-700 dark:text-gray-200"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Permission Group
            </label>
            <Select
              options={permissionGroups}
              value={formData.selectedPermissionGroup}
              onChange={(value) =>
                handleChange("selectedPermissionGroup", value)
              }
              isClearable
              isDisabled={loadError}
              styles={customStyles}
              placeholder={
                loadError ? "Default applied" : "Select a Permission Group"
              }
            />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Name
            </label>
            <input
              type="text"
              className="border rounded p-2 w-full h-12 dark:bg-gray-700 dark:text-gray-200"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Surname
            </label>
            <input
              type="text"
              className="border rounded p-2 w-full h-12 dark:bg-gray-700 dark:text-gray-200"
              value={formData.surname1}
              onChange={(e) => handleChange("surname1", e.target.value)}
            />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Password
            </label>
            <input
              type="password"
              className="border rounded p-2 w-full h-12 dark:bg-gray-700 dark:text-gray-200"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
            />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Repeat Password
            </label>
            <input
              type="password"
              className="border rounded p-2 w-full h-12 dark:bg-gray-700 dark:text-gray-200"
              value={formData.repeatPassword}
              onChange={(e) => handleChange("repeatPassword", e.target.value)}
            />
          </div>
        </div>
        {error && (
          <div className="text-center my-4">
            <div className="inline-block bg-red-100 text-red-600 px-4 py-2 rounded-md text-sm font-medium">
              {error}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserAdd;
